import type { PlaybookTestCallSetup } from "@repo/app-types/entities/playbookTestCase";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Checkbox } from "@repo/ui/Checkbox/checkbox";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { SlideoverContainer } from "@repo/ui/Slideover/slideoverContainer";
import {
  SlideoverDialog,
  SlideoverDialogButtons,
  SlideoverDialogContent,
  SlideoverDialogTitleHeader,
} from "@repo/ui/Slideover/slideoverDialog";
import { IconPhone, IconRefresh } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { Dialog, DialogTrigger } from "react-aria-components";
import {
  describePlaybookTestCallSetup,
  dropIncompletePlaybookTestCallSetupFields,
  PlaybookTestCallSetupEditor,
} from "./playbookTestCallSetupEditor.js";
import { TeammateAgentSimulation } from "./teammateAgentSimulation.js";
import type { TeammateAgentConfigController } from "./useTeammateAgentConfigController.js";

export interface AgentSimulationSlideoverProps {
  controller: TeammateAgentConfigController;
}

interface AgentSimulationCallSetupControlProps {
  /** The setup the current conversation was started with. */
  callSetup: PlaybookTestCallSetup | null;
  isOutboundAvailable: boolean;
  onApply: (callSetup: PlaybookTestCallSetup | null) => void;
}

interface AgentSimulationCallSetupDialogContentProps {
  callSetup: PlaybookTestCallSetup | null;
  isOutboundAvailable: boolean;
  onClose: () => void;
  onApply: (callSetup: PlaybookTestCallSetup | null) => void;
}

/**
 * The editor as it appears in the modal: it edits a draft of its own, so
 * closing without applying leaves the running conversation alone.
 */
const AgentSimulationCallSetupDialogContent: FC<
  AgentSimulationCallSetupDialogContentProps
> = (props) => {
  const { callSetup, isOutboundAvailable, onClose, onApply } = props;

  const [draft, setDraft] = useState<PlaybookTestCallSetup | null>(callSetup);

  return (
    <>
      <ModalHeader isCloseVisible>Call setup</ModalHeader>
      <div className="mt-4">
        <PlaybookTestCallSetupEditor
          value={draft}
          onChange={setDraft}
          isOutboundAvailable={isOutboundAvailable}
        />
      </div>
      <div className="mt-4 flex items-center justify-end gap-3 border-t border-border-light pt-4">
        <p className="mr-auto text-sm text-muted-foreground">
          Applying starts a new conversation.
        </p>
        <Button variant={ButtonVariant.Secondary} onClick={onClose}>
          Cancel
        </Button>
        <Button variant={ButtonVariant.Primary} onClick={() => onApply(draft)}>
          Apply
        </Button>
      </div>
    </>
  );
};

/**
 * What the agent is told before the call, as the chat is using it, with a way
 * to change it. The modal mounts its contents only while open, so an abandoned
 * edit starts over on the next open.
 */
const AgentSimulationCallSetupControl: FC<
  AgentSimulationCallSetupControlProps
> = (props) => {
  const { callSetup, isOutboundAvailable, onApply } = props;

  return (
    <DialogTrigger>
      <div className="flex items-center gap-3 rounded-lg border border-border-light bg-white px-3 py-2">
        <IconPhone className="size-4 shrink-0 text-muted-foreground" />
        <div className="shrink-0 text-sm font-medium text-foreground">
          Call setup
        </div>
        <div className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
          {describePlaybookTestCallSetup(callSetup)}
        </div>
        <Button variant={ButtonVariant.Secondary} size={Size.SMALL}>
          Edit
        </Button>
      </div>
      <CenteredModal isDismissable size={Size.LARGE}>
        <Dialog className="w-full max-w-2xl px-4 pt-5 pb-4 text-left outline-hidden sm:p-6">
          {({ close }) => (
            <AgentSimulationCallSetupDialogContent
              callSetup={callSetup}
              isOutboundAvailable={isOutboundAvailable}
              onClose={close}
              onApply={(appliedCallSetup) => {
                onApply(appliedCallSetup);
                close();
              }}
            />
          )}
        </Dialog>
      </CenteredModal>
    </DialogTrigger>
  );
};

/**
 * The "Try this agent" slideover: a live preview chat with the agent using the
 * latest draft config. Shared by the legacy Configuration tab and the static
 * agent layout.
 */
export const AgentSimulationSlideover: FC<AgentSimulationSlideoverProps> = (
  props,
) => {
  const { controller } = props;
  const {
    teammateAgentId,
    isSimulationSlideoverOpen,
    setIsSimulationSlideoverOpen,
    simulationKey,
    shouldUseLiveToolCalls,
    setShouldUseLiveToolCalls,
    handleResetSimulation,
    isOutboundCallingAvailable,
  } = controller;

  // What the chat was started with. The prompt prefix cannot change under a
  // running conversation, so applying a setup remounts the chat with it.
  const [appliedCallSetup, setAppliedCallSetup] =
    useState<PlaybookTestCallSetup | null>(null);
  const [callSetupGeneration, setCallSetupGeneration] = useState(0);

  return (
    <SlideoverContainer
      size={Size.SMALL}
      isDismissable
      isOpen={isSimulationSlideoverOpen}
      onOpenChange={(newIsOpen) => {
        if (!newIsOpen) {
          setIsSimulationSlideoverOpen(false);
          setShouldUseLiveToolCalls(false);
        }
      }}
    >
      <SlideoverDialog label="Try this agent">
        <SlideoverDialogTitleHeader isLoading={false}>
          <div className="flex w-full items-center justify-between">
            <div>
              <Label size={Size.MEDIUM}>Try this agent</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Chat with your agent to preview how it responds.
              </p>
            </div>
          </div>
        </SlideoverDialogTitleHeader>
        <SlideoverDialogContent>
          <div className="flex h-full flex-col overflow-hidden">
            <div className="shrink-0 border-b border-border-light p-4">
              <AgentSimulationCallSetupControl
                callSetup={appliedCallSetup}
                isOutboundAvailable={isOutboundCallingAvailable}
                onApply={(callSetup) => {
                  const applied =
                    dropIncompletePlaybookTestCallSetupFields(callSetup);
                  // Applying what the conversation already runs on is not a
                  // reason to throw the conversation away.
                  if (
                    JSON.stringify(applied) === JSON.stringify(appliedCallSetup)
                  ) {
                    return;
                  }
                  setAppliedCallSetup(applied);
                  setCallSetupGeneration((generation) => generation + 1);
                }}
              />
            </div>
            <div className="flex min-h-0 flex-auto flex-col overflow-hidden p-4">
              <TeammateAgentSimulation
                key={`${simulationKey}-${callSetupGeneration}`}
                teammateAgentId={teammateAgentId}
                shouldUseLiveToolCalls={shouldUseLiveToolCalls}
                callSetup={appliedCallSetup}
              />
            </div>
          </div>
        </SlideoverDialogContent>
        <SlideoverDialogButtons>
          <div className="flex flex-1 items-center">
            <Checkbox
              isChecked={shouldUseLiveToolCalls}
              onChange={setShouldUseLiveToolCalls}
              label="Use live tool calls"
            />
          </div>
          <Button
            variant={ButtonVariant.Secondary}
            onClick={handleResetSimulation}
            icon={<IconRefresh className="h-4 w-4" />}
          >
            Reset
          </Button>
          <Button
            variant={ButtonVariant.Primary}
            onClick={() => setIsSimulationSlideoverOpen(false)}
          >
            Done
          </Button>
        </SlideoverDialogButtons>
      </SlideoverDialog>
    </SlideoverContainer>
  );
};
